import * as React from "react";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

import { cn } from "@/lib/utils";
import { Button } from "@/components/atoms/button";
import { Calendar } from "@/components/atoms/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/atoms/popover";

interface DatePickerProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  id?: string;
  minDate?: Date;
  maxDate?: Date;
}

// Helper function to safely parse date strings in YYYY-MM-DD format
const parseValidDate = (dateString: string): Date | undefined => {
  if (!dateString) return undefined;

  // Check if it matches YYYY-MM-DD format
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateString)) return undefined;

  const parts = dateString.split("-");
  if (parts.length !== 3) return undefined;

  const [yearStr, monthStr, dayStr] = parts;
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  const day = parseInt(dayStr, 10);

  // Check if parsing resulted in valid numbers
  if (isNaN(year) || isNaN(month) || isNaN(day)) return undefined;

  // Create the date
  const date = new Date(year, month - 1, day);

  // Verify the date is valid and the components match what we intended
  // This catches cases like February 30th or month 13
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return undefined;
  }

  return date;
};

export function DatePicker({
  value,
  onChange,
  placeholder = "Seleccionar fecha",
  className,
  disabled = false,
  id,
  minDate,
  maxDate,
}: DatePickerProps) {
  const [date, setDate] = React.useState<Date | undefined>(() => {
    return parseValidDate(value || "");
  });
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const parsedDate = parseValidDate(value || "");
    setDate(parsedDate);
  }, [value]);

  const handleSelect = React.useCallback(
    (selectedDate: Date | undefined) => {
      setDate(selectedDate);
      if (selectedDate && onChange) {
        // Format as YYYY-MM-DD using local date components to avoid timezone issues
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
        const day = String(selectedDate.getDate()).padStart(2, "0");
        onChange(`${year}-${month}-${day}`);
      } else if (!selectedDate && onChange) {
        onChange("");
      }
      setOpen(false);
    },
    [onChange],
  );

  const isDateDisabled = React.useCallback(
    (checkDate: Date) => {
      if (minDate && checkDate < minDate) return true;
      if (maxDate && checkDate > maxDate) return true;
      return false;
    },
    [minDate, maxDate],
  );

  const handleOpenChange = React.useCallback(
    (newOpen: boolean) => {
      if (!disabled) {
        setOpen(newOpen);
      }
    },
    [disabled],
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal border-gray-200 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
            !date && "text-muted-foreground",
            disabled && "cursor-not-allowed opacity-50",
            className,
          )}
          disabled={disabled}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="dialog"
        >
          <CalendarIcon className="mr-2 hidden sm:block h-4 w-4 flex-shrink-0" />
          <span className="truncate">
            {date ? format(date, "PPP", { locale: es }) : placeholder}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto bg-white p-0 shadow-lg border border-gray-200 rounded-md z-[1000]"
        align="start"
        side="bottom"
        sideOffset={4}
        avoidCollisions={true}
        onInteractOutside={() => {
          // Close when clicking outside the calendar
          setOpen(false);
        }}
        onEscapeKeyDown={() => setOpen(false)}
      >
        <Calendar
          mode="single"
          selected={date}
          onSelect={handleSelect}
          initialFocus
          fromDate={minDate}
          toDate={maxDate}
          disabled={isDateDisabled}
          className="rounded-md border-0 select-none"
          classNames={{
            day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 select-none cursor-pointer hover:bg-accent hover:text-accent-foreground rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 data-[selected-single=true]:!bg-indigo-600 data-[selected-single=true]:!text-white data-[range-start=true]:!bg-indigo-600 data-[range-start=true]:!text-white data-[range-end=true]:!bg-indigo-600 data-[range-end=true]:!text-white",
            nav_button:
              "h-7 w-7 bg-transparent p-0 hover:bg-gray-100 rounded-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-600",
            nav_button_previous: "absolute left-1",
            nav_button_next: "absolute right-1",
            table: "w-full border-collapse space-y-1",
            head_row: "flex",
            head_cell:
              "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem] flex items-center justify-center",
            row: "flex w-full mt-2",
            cell: "text-center text-sm p-0 relative [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20 flex items-center justify-center",
            day_selected:
              "bg-indigo-600 text-white hover:bg-indigo-700 hover:text-white focus:bg-indigo-600 focus:text-white",
            day_today:
              "bg-accent text-accent-foreground font-semibold aria-selected:bg-indigo-600 aria-selected:text-white",
            day_outside: "text-muted-foreground opacity-50 cursor-default",
            day_disabled:
              "text-muted-foreground opacity-30 cursor-not-allowed hover:bg-transparent",
            day_range_middle:
              "aria-selected:bg-accent aria-selected:text-accent-foreground",
            day_hidden: "invisible",
          }}
          style={{
            // Ensure proper click handling
            WebkitUserSelect: "none",
            MozUserSelect: "none",
            msUserSelect: "none",
            userSelect: "none",
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
